import React, { ForwardRefRenderFunction, forwardRef } from "react";
import { flushSync } from "react-dom";
//#region types
import { DimensionEnums } from "models/enums";
import { FormHandles, SubmitFunc } from "models/propTypes/genericFormProps";
import {
  IncomingInvoiceDetailsFormViewProps,
  PeriodisationFieldProps,
} from "models/propTypes/incomingInvoicePropTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants, InvoiceConstants, TodoConstants } from "utils/constants";
import { IncomingInvoiceFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FileUploaderProps, SelectInputProps } from "models/propTypes/wintFormPropsTypes";
//#endregion
//#region  components
import { Grid, Link } from "@mui/material";
import {
  DimensionChipSelector,
  FileUploader,
  FormFieldLayout,
  HorizontalFormField,
  SelectInput,
  Toggle,
  unconnectedFormField,
  useWintForm,
  WintForm,
} from "components/common/wintForms";
import TextField from "components/common/wintForms/detailsForm/textField";
import DetailsFormSection from "components/common/wintForms/detailsForm/detailsFormSection";
import SearchField from "components/common/wintForms/detailsForm/searchField";
import CurrencyField from "components/common/wintForms/detailsForm/currencyField";
import CurrencySelectionField from "components/common/wintForms/detailsForm/currencySelectionField";
import DateField from "components/common/wintForms/detailsForm/dateField";
import NumberFieldWithSuggestions from "components/common/wintForms/detailsForm/numberFieldWithSuggestions";
import ToggleField from "components/common/wintForms/detailsForm/toggleField";
import { InfoTooltip, PeriodisationField, StaticInfoMessage, useNavigationHistory } from "components/common";
import useIncomingInvoiceTodoFormHelpers from "./useTodoFormHelpers";
import IncomingInvoicePeriodSection from "./periodisationSection";
import { CostForwardingDetailsSection } from "components/costForwardingMarking";
import useSplitPeriodisationRemovalConfirmation from "./useSplitPeriodisationRemovalConfirmation";
import { useIncomingInvoiceService } from "serviceHooks";
//#endregion
import {
  LocalizationHelperInstance,
  DateHelper,
  UrlHelper,
  MathHelper,
  CountryHelper,
  PeriodisationRowsHelper,
} from "utils/helpers";
import { useSelector } from "react-redux";
import { AppState } from "store";

const IncomingInvoiceDetailsForm: ForwardRefRenderFunction<FormHandles, IncomingInvoiceDetailsFormViewProps> = (
  props,
  ref
) => {
  const incomingInvoiceService = useIncomingInvoiceService();
  const {
    invoice,
    dimensionAmount,
    setDimensionAmount,
    accountSuggestions,
    getBookingTreeItems,
    updatePaymentClickHandler,
    fileViewer,
    companyNoVat,
    isReadOnly,
    isUnhandled,
    showDimensions,
    hasPermissionToEditPaymentInformation,
    loadInvoice,
  } = props;

  const formRef = React.useRef<FormHandles>(null);
  const { wintFormProps, connect, values, setValues } = useWintForm<IncomingInvoiceFormTypes.Details>({
    formRef,
    ...props,
    enableReinitialize: true,
    validateOnBlur: true,
    handleSubmit: props.handleSubmit,
  });

  const { getRouteStateValue, removeRouteStateValue } = useNavigationHistory();
  const { getAccountAdditionalInfoType } = useIncomingInvoiceTodoFormHelpers(invoice);
  const noBankIntegration = useSelector((store: AppState) => store.auth.selectedCompany.noBankIntegration);
  const confirmSplitRemoval = useSplitPeriodisationRemovalConfirmation();

  React.useEffect(() => {
    const state: IncomingInvoiceFormTypes.Todo | undefined = getRouteStateValue(TodoConstants.RouteTodoSelectionKey);
    const mapToDetailFormObject = (todoFormObject: IncomingInvoiceFormTypes.Todo) => {
      return {
        ...todoFormObject,
      };
    };
    setTimeout(() => {
      state && setValues((prevState) => ({ ...prevState, ...mapToDetailFormObject(state) }));
    });

    if (state) removeRouteStateValue(TodoConstants.RouteTodoSelectionKey);
  }, []);

  const [allowPeriodisation, setAllowPeriodisation] = React.useState(invoice.allowPeriodisation);

  React.useEffect(() => {
    if (!isUnhandled && values.amount && values.currency) {
      incomingInvoiceService
        .getPeriodisationsAllowed(values.amount, values.currency, values.invoiceDate)
        .then((res) => setAllowPeriodisation(res));
    } else {
      setAllowPeriodisation(false);
    }
  }, [values.amount, values.currency, values.invoiceDate]);

  React.useEffect(() => {
    setDimensionAmount(PeriodisationRowsHelper.netAmount(+values.amount, values.vat, companyNoVat));
  }, [values.amount, values.vat]);

  // Validation rejects a split whose rows no longer add up to the net, so before saving the user chooses whether the
  // save drops it. Changing Belopp or Moms alone leaves the split in place, so the user can still change the amount back.
  const submit: SubmitFunc = async (...args) => {
    const net = PeriodisationRowsHelper.netAmount(+values.amount, values.vat, companyNoVat);
    if (PeriodisationRowsHelper.isSplitStaleForNet(values.periodisation, net)) {
      // Rejected the way a failed validation is, which every caller already treats as not saved.
      if (!(await confirmSplitRemoval())) throw null;
      // The form's submit reads the values it was rendered with, so the cleared value has to render first.
      flushSync(() =>
        formRef.current!.setFieldValue!("periodisation", {
          mode: values.periodisation!.mode,
          isSplit: false,
          rows: [{}],
        })
      );
    }
    return formRef.current!.submit(...args);
  };

  React.useImperativeHandle(ref, () => ({ ...formRef.current!, submit }));

  const invoiceDateConnector = connect("invoiceDate");
  const dueDateConnector = connect("dueDate");
  const paymentTermsConnector = connect("paymentTerms");
  const isInternationalPaymentDetailsAcceptedConnector = connect("isInternationalPaymentDetailsAccepted");

  const onPaymentDatesSetValue = (invoiceDate?: Date, paymentDate?: Date) => {
    if (!invoiceDate || !paymentDate) return;

    const diffDays = paymentDate && invoiceDate ? DateHelper.getDiffDays(invoiceDate, paymentDate) : undefined;
    paymentTermsConnector.setTouched();
    paymentTermsConnector.setValue(diffDays !== undefined && diffDays >= 0 ? diffDays : undefined);
  };

  const onInvoiceDateSetValue = (value?: Date) => {
    invoiceDateConnector.setValue(value);
    const paymentDate = values.dueDate;

    onPaymentDatesSetValue(value, paymentDate);
  };

  const onDueDateSetValue = (value?: Date) => {
    dueDateConnector.setValue(value);
    const invoiceDate = values.invoiceDate;

    onPaymentDatesSetValue(invoiceDate, value);
  };

  const onPaymentTermsSetValue = (value?: number) => {
    paymentTermsConnector.setValue(value);

    const invoiceDate = values.invoiceDate;
    if (!invoiceDate) return;

    dueDateConnector.setTouched();
    dueDateConnector.setValue(isNaN(value as number) ? undefined : DateHelper.addDays(invoiceDate, +value!));
  };

  const onPaymentTermsChange = (e: React.ChangeEvent<any>) => {
    onPaymentTermsSetValue(e.target.value === undefined ? undefined : Number(e.target.value));
  };

  const onPaymentDetailsAcceptedSetValue = (value?: boolean) => {
    isInternationalPaymentDetailsAcceptedConnector.setValue(value);
    props.onPaymentDetailsAcceptHandler(!!value);
  };

  const ReadOnlyDimensionLayout = React.useCallback(function ReadOnlyDimensionLayout(props: any) {
    return (
      <Grid item sm={6}>
        <FormFieldLayout direction="vertical" {...props}></FormFieldLayout>
      </Grid>
    );
  }, []);

  return (
    <WintForm {...wintFormProps}>
      <DetailsFormSection
        header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.General)}
        icon={IconDefinitionConstants.WInfo}
        readonly={isReadOnly}
      >
        <TextField
          label={TranslationKeys.Common.Sender}
          {...connect("supplierName")}
          disabled
          href={UrlHelper.formatPath(`/Supplier/${invoice.supplier?.id}`)}
          readonly={isReadOnly}
        />
        <TextField label={TranslationKeys.Common.CIN} {...connect("supplierOrgNr")} disabled readonly={isReadOnly} />
        <SearchField
          label={TranslationKeys.Common.RefersTo}
          helperText={TranslationKeys.IncomingInvoice.AccountRequiredInfoMessage}
          {...connect("account")}
          items={getBookingTreeItems}
          suggestedItems={accountSuggestions}
          disabled={isUnhandled || invoice.isAutoInvoice}
          readonly={isReadOnly}
        />
        {!invoice.isAutoInvoice &&
        getAccountAdditionalInfoType(values.account?.id) !==
          WintSuperkollAPIModel.AdditionalInformationRequired.None ? (
          <TextField
            label={TranslationKeys.Invoice.AdditionalInformationComment}
            {...connect("additionalInfoComment")}
            readonly={isReadOnly}
            disabled={isUnhandled}
          />
        ) : (
          <></>
        )}
        {!invoice.isAutoInvoice &&
          !isReadOnly &&
          getAccountAdditionalInfoType(values.account?.id) ===
            WintSuperkollAPIModel.AdditionalInformationRequired.FileOrComment && (
            <HorizontalFormField<FileUploaderProps>
              Component={FileUploader}
              {...connect("additionalInfoAttachments")}
              label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Attachments)}
              descriptionText=""
              disabled={isUnhandled}
            />
          )}
        <TextField
          label={TranslationKeys.Common.Reference}
          {...connect("referencePerson")}
          maxLength={128}
          readonly={isReadOnly}
          disabled={isUnhandled}
        />
        <TextField
          label={TranslationKeys.Common.OCRInvoiceNum}
          descriptionText={TranslationKeys.IncomingInvoice.OCRText}
          {...connect("ocr")}
          maxLength={128}
          readonly={isReadOnly}
          disabled={isUnhandled}
        />
      </DetailsFormSection>
      <DetailsFormSection
        header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Amount)}
        icon={IconDefinitionConstants.WCalculator}
        readonly={isReadOnly}
      >
        <CurrencyField
          label={TranslationKeys.Common.AmountInclVAT}
          descriptionText={TranslationKeys.IncomingInvoice.AmountHelpText}
          {...connect("amount")}
          currency={values.currency}
          isCredit={values.isCredit}
          readonly={isReadOnly}
          currencyDisplay="narrowSymbol"
          disabled={isUnhandled}
        />
        <CurrencyField
          label={TranslationKeys.Common.VAT}
          descriptionText={TranslationKeys.IncomingInvoice.VATHelpText}
          {...connect("vat")}
          currency={values.currency}
          isCredit={values.isCredit}
          readonly={isReadOnly}
          currencyDisplay="narrowSymbol"
          disabled={isUnhandled}
        />
        <CurrencyField
          label={TranslationKeys.Common.AmountExVat}
          descriptionText={TranslationKeys.IncomingInvoice.AmountExVATHelpText}
          {...unconnectedFormField}
          value={MathHelper.subtractAmounts(+(values.amount ?? 0), values.vat ?? 0)}
          currency={values.currency}
          isCredit={values.isCredit}
          readonly
          readonlyPassThrough={!isReadOnly}
          currencyDisplay="narrowSymbol"
          disabled={isUnhandled}
        />
        <CurrencySelectionField
          label={TranslationKeys.Common.Currency}
          descriptionText={TranslationKeys.IncomingInvoice.CurrencyHelpText}
          {...connect("currency")}
          readonly={isReadOnly}
          disabled={isUnhandled}
        />
      </DetailsFormSection>
      {!isUnhandled && (
        <DetailsFormSection
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Dates)}
          icon={IconDefinitionConstants.WCalendar}
          readonly={isReadOnly}
        >
          <DateField
            label={TranslationKeys.Common.InvoiceDate}
            descriptionText={TranslationKeys.IncomingInvoice.InvoiceDateHelpText}
            {...invoiceDateConnector}
            setValue={onInvoiceDateSetValue}
            readonly={isReadOnly}
          />
          <DateField
            label={TranslationKeys.Common.DueDate}
            descriptionText={TranslationKeys.IncomingInvoice.DueDateHelpText}
            {...dueDateConnector}
            setValue={onDueDateSetValue}
            minDate={values.invoiceDate}
            readonly={isReadOnly}
          />
          <DateField
            label={TranslationKeys.Common.PaymentDate}
            descriptionText={TranslationKeys.IncomingInvoice.PaymentDateHelpText}
            {...connect("paymentDate")}
            minDate={DateHelper.today()}
            readonly={isReadOnly}
          />
        </DetailsFormSection>
      )}
      {!isUnhandled && (
        <DetailsFormSection
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.PaymentDetails)}
          icon={IconDefinitionConstants.WTransactions}
          readonly={isReadOnly}
        >
          <TextField label={TranslationKeys.Common.Bankgiro} {...connect("bg")} disabled readonly={isReadOnly} />
          <NumberFieldWithSuggestions
            label={TranslationKeys.Common.PaymentTerms}
            {...paymentTermsConnector}
            onChange={onPaymentTermsChange}
            setValue={onPaymentTermsSetValue}
            allowNegative={false}
            suffix={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Days)}
            suggestions={InvoiceConstants.PaymentTerms}
            readonly={isReadOnly}
          />
          <TextField
            label={TranslationKeys.Common.PaymentStatus}
            descriptionText={
              <>
                <span>
                  {LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.IncomingInvoice.PaymentTermsHelpText
                  )}{" "}
                </span>
                <Link onClick={updatePaymentClickHandler.bind(null, values)}>
                  {LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.IncomingInvoice.PaymentTermsHelpTextLink
                  )}
                </Link>
              </>
            }
            {...connect("paymentStatus")}
            disabled
            readonly={isReadOnly}
          />
          <ToggleField
            label={TranslationKeys.Common.IsCreditInvoice}
            descriptionText={TranslationKeys.IncomingInvoice.IsCreditInvoiceHelpText}
            {...connect("isCredit")}
            readonly={isReadOnly}
          />
          {noBankIntegration ? (
            <>
              <FormFieldLayout
                direction="horizontal"
                label={TranslationKeys.Common.IsAlreadyPaid}
                descriptionText={TranslationKeys.IncomingInvoice.AlreadyPaidDescription}
              >
                <Grid container direction="row" alignItems="center" wrap="nowrap" spacing={1}>
                  <Grid item>
                    <Toggle {...connect("isAlreadyPaid")} disabled={true} readonly={isReadOnly} />
                  </Grid>
                  <Grid item>
                    <InfoTooltip
                      text={LocalizationHelperInstance.getFormattedMessage(
                        TranslationKeys.IncomingInvoice.NoBankIntegrationToolTip
                      )}
                      icon={IconDefinitionConstants.WInfo}
                      placement="top"
                    />
                  </Grid>
                </Grid>
              </FormFieldLayout>
              <Grid container mt={2}>
                <Grid item>
                  <StaticInfoMessage gutterBottom>
                    {LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.IncomingInvoice.NoBankIntegrationInfoMessage
                    )}
                  </StaticInfoMessage>
                </Grid>
              </Grid>
            </>
          ) : (
            <ToggleField
              label={TranslationKeys.Common.IsAlreadyPaid}
              descriptionText={TranslationKeys.IncomingInvoice.AlreadyPaidDescription}
              {...connect("isAlreadyPaid")}
              readonly={isReadOnly}
              disabled={false}
            />
          )}
          {invoice.isInternationalPayment && hasPermissionToEditPaymentInformation && (
            <>
              <TextField label={TranslationKeys.IncomingInvoice.IBAN} {...connect("iban")} readonly={isReadOnly} />
              {!invoice.isSEPAPayment && (
                <TextField
                  label={TranslationKeys.IncomingInvoice.BicOrSwift}
                  {...connect("bic")}
                  readonly={isReadOnly}
                />
              )}
              <DetailsFormSection
                header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.IncomingInvoice.SupplierAddress)}
                description={LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.IncomingInvoice.SupplierPostalAddressSubText
                )}
                icon={IconDefinitionConstants.WPin}
                readonly={isReadOnly}
              >
                <TextField
                  label={TranslationKeys.Common.StreetAddress}
                  {...connect("street")}
                  maxLength={100}
                  readonly={isReadOnly}
                />
                <TextField
                  label={TranslationKeys.Common.StreetAddress2}
                  {...connect("street2")}
                  maxLength={100}
                  readonly={isReadOnly}
                />
                <TextField
                  label={TranslationKeys.Common.PostalCode}
                  {...connect("zipCode")}
                  maxLength={12}
                  readonly={isReadOnly}
                />
                <TextField
                  label={TranslationKeys.Common.City}
                  {...connect("city")}
                  maxLength={100}
                  readonly={isReadOnly}
                />
                {isReadOnly ? (
                  <TextField
                    label={TranslationKeys.Common.Country}
                    {...connect("country")}
                    value={values.country ? LocalizationHelperInstance.getFormattedMessage(values.country) : ""}
                    readonly
                  />
                ) : (
                  <HorizontalFormField<SelectInputProps>
                    Component={SelectInput}
                    {...connect("country")}
                    label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Country)}
                    items={CountryHelper.getCountrySelectionList()}
                  />
                )}
                {!isReadOnly && (
                  <ToggleField
                    label={TranslationKeys.IncomingInvoice.InternationalPaymentAndAddressToggleLabel}
                    {...isInternationalPaymentDetailsAcceptedConnector}
                    setValue={onPaymentDetailsAcceptedSetValue}
                  />
                )}
              </DetailsFormSection>
            </>
          )}
        </DetailsFormSection>
      )}
      {!isUnhandled && showDimensions && (!isReadOnly || values.dimensions.length > 0) && (
        <DetailsFormSection
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Tags)}
          icon={IconDefinitionConstants.WHashtag}
          readonly={isReadOnly}
        >
          <DimensionChipSelector
            {...connect("dimensions")}
            type={DimensionEnums.DimensionCategory.IncomingInvoice}
            amount={dimensionAmount}
            currency={values.currency}
            layout={isReadOnly ? <ReadOnlyDimensionLayout /> : <FormFieldLayout direction="horizontal" />}
            allowDefaultDimensions={invoice.allowDimensionDefaults}
            fileViewer={fileViewer}
            readonly={isReadOnly}
          />
        </DetailsFormSection>
      )}
      {allowPeriodisation && !isReadOnly && (
        <DetailsFormSection
          header={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Accrual)}
          icon={IconDefinitionConstants.WMonthCalendar}
          readonly={isReadOnly}
        >
          <HorizontalFormField<PeriodisationFieldProps>
            Component={PeriodisationField}
            {...connect("periodisation")}
            label={TranslationKeys.Common.Period}
            descriptionText={TranslationKeys.IncomingInvoice.AccuralHelpText}
            netAmount={Math.abs(dimensionAmount)}
            currency={values.currency}
            splitSupported={PeriodisationRowsHelper.isSplitSupported(invoice)}
            hideErrorText
            fileViewer={fileViewer}
            invoiceMonth={PeriodisationRowsHelper.invoiceMonth(values.invoiceDate)}
            isCredit={values.isCredit}
          />
        </DetailsFormSection>
      )}
      {allowPeriodisation && isReadOnly && (
        <IncomingInvoicePeriodSection invoice={invoice} loadInvoice={loadInvoice} fileViewer={fileViewer} />
      )}
      <CostForwardingDetailsSection isReadOnlyForm={isReadOnly} />
    </WintForm>
  );
};

export default forwardRef(IncomingInvoiceDetailsForm);
